import { useEffect, useState } from "react";
import Avatar from "../common/Avatar";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { lawyerService } from "../../services/api";
import useTranslate from "../../hooks/useTranslate";
import { resolveCountry, Flag } from "./lawyerFlag";
import {
  CloseIcon,
  BackArrowIcon,
  ChatBubbleIcon,
  CheckmarkIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
} from "./v2/icons";
import { LAW_SCRIM } from "./v2/tokens";

const asList = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
const isDualRole = (l) =>
  l.is_dual === true ||
  asList(l.roles).some((r) => String(r).toLowerCase() === "agent");

// Uppercase eyebrow section header — 1f desktop (11px/700, mb 7px) and
// 1m mobile (10.5px/700, mb 6px), letter-spacing .6px, #98A2B3.
const Section = ({ title, mobile = false, children }) => (
  <div>
    <h4
      className={`font-bold uppercase tracking-[.6px] text-[#98A2B3] ${
        mobile ? "text-[10.5px] mb-[6px]" : "text-[11px] mb-[7px]"
      }`}
    >
      {title}
    </h4>
    {children}
  </div>
);

// Full lawyer profile — rebuilt 1:1 from design handoff artboards 1f (desktop
// side panel over the grid) and 1m (mobile full-screen with sticky contact
// bar). Logic untouched: fetches the detail payload (license_number lives
// here, not on the grid card) and merges it over the summary passed from the
// card for an instant header.
const LawyerDetail = ({ lawyer, onClose, onContact, isSelf = false, isPending = false, t: tProp }) => {
  const { t: tHook } = useTranslate();
  const t = tProp || tHook;
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  // Always the Lawyer model PK — /lawyer/<pk>/ is keyed by `id`, NOT account_id
  // (an account_id fallback could 404 or resolve a different lawyer).
  const lawyerId = lawyer?.id;

  useEffect(() => {
    if (!lawyerId) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      const res = await lawyerService.getDetail(lawyerId, controller.signal);
      if (res.success) setDetail(res.data || {});
      setLoading(false);
    })().catch(() => setLoading(false));
    return () => controller.abort();
  }, [lawyerId]);

  if (!lawyer) return null;

  const l = { ...lawyer, ...(detail || {}) };
  const verified = l.is_verified === true;
  const dual = isDualRole(l);
  const specializations = asList(l.specializations).length
    ? asList(l.specializations)
    : (l.specialization || "").split(",").map((s) => s.trim()).filter(Boolean);
  const jurisdictionValues = asList(l.jurisdictions).length
    ? asList(l.jurisdictions)
    : l.country
    ? [l.country]
    : [];
  const jurisdictions = jurisdictionValues.map(resolveCountry);
  const languages = asList(l.languages);
  const hq = [l.city, l.country].filter(Boolean).join(", ");
  const firmLine = l.firm || hq;
  // Phone is REMOVED from all lawyer surfaces (Marin product decision
  // 2026-07-23) — `mobile` is never rendered even when the API sends it.
  const hasContacts = !!l.email;

  // Badge row — verified badge per 1f (card style: 11.5px/600) and 1m
  // (11px, check 10×10). Dual-role pill carried from the card level (1e
  // "Agent & Lawyer" purple pill) — not drawn on the detail boards, see
  // DECISIONS D-3 / open question for Marin.
  const badgeRow = (verified || dual) && (
    <div className={`flex flex-wrap items-center gap-[6px] ${isMobile ? "mt-[6px]" : "mt-[7px]"}`}>
      {verified && (
        <span
          className={`inline-flex items-center gap-1 rounded-full bg-[#EBF3FF] text-[#1567D2] border border-[#C9E1FF] px-[9px] py-[3px] font-semibold ${
            isMobile ? "text-[11px]" : "text-[11.5px]"
          }`}
        >
          <CheckmarkIcon size={isMobile ? 10 : 11} />
          {t("Verified Lawyer")}
        </span>
      )}
      {dual && (
        <span
          className={`inline-flex items-center rounded-full bg-[#F4F1FE] text-[#6941C6] border border-[#E4DBFB] px-[9px] py-[3px] font-semibold ${
            isMobile ? "text-[11px]" : "text-[11.5px]"
          }`}
        >
          {t("Agent & Lawyer")}
        </span>
      )}
    </div>
  );

  // Opt-in contact row — email only (phone removed by decision, see above).
  // Renders only when the lawyer exposed their email (chat-only variant = no
  // row; the column gap keeps the layout gap-free). 1f: centered, 12.5px.
  // 1m: left-aligned, 12px.
  const contactRow = hasContacts && (
    <div
      className={`flex flex-wrap gap-4 text-[#475467] ${
        isMobile ? "text-[12px]" : "justify-center text-[12.5px]"
      }`}
    >
      <span className="inline-flex items-center gap-[5px] min-w-0">
        <EnvelopeIcon size={12} />
        <span className="truncate">{l.email}</span>
      </span>
    </div>
  );

  // Primary CTA / pending variant. 1f panel: 11px 0, 14px, radius 9, chat
  // 15×15. 1m sticky bar: 13px 0, 14.5px, radius 11 (no hover on mobile
  // per handoff). Pending state (not drawn in the handoff) keeps its
  // existing disabled presentation on the artboard button geometry.
  const ctaButton = (mobile) =>
    isPending ? (
      <button
        type="button"
        disabled
        className={`w-full font-semibold border border-[#DDE3EC] text-[#98A2B3] bg-[#F7F8FA] cursor-not-allowed ${
          mobile ? "rounded-[11px] py-[13px] text-[14.5px]" : "rounded-[9px] py-[11px] text-[14px]"
        }`}
      >
        {t("Request pending")}
      </button>
    ) : (
      <button
        type="button"
        onClick={() => onContact(l)}
        className={`w-full flex items-center justify-center gap-[7px] bg-[#2283FF] text-white font-semibold cursor-pointer ${
          mobile
            ? "rounded-[11px] py-[13px] text-[14.5px]"
            : "rounded-[9px] py-[11px] text-[14px] hover:bg-[#1570E8]"
        }`}
      >
        <ChatBubbleIcon size={15} />
        {t("Contact Lawyer")}
      </button>
    );

  // Detail-only sections (About / Specializations / two-column / license box).
  const sections = loading && !detail ? (
    <div className="space-y-3 animate-pulse">
      <div className="h-3.5 bg-[#EEF1F6] rounded w-1/3" />
      <div className="h-3 bg-[#EEF1F6] rounded w-full" />
      <div className="h-3 bg-[#EEF1F6] rounded w-5/6" />
    </div>
  ) : (
    <>
      {l.bio && (
        <Section title={t("About")} mobile={isMobile}>
          <p className="text-[13px] text-[#475467] leading-[1.55] whitespace-pre-line">
            {l.bio}
          </p>
        </Section>
      )}

      {specializations.length > 0 && (
        <Section title={t("Specializations")} mobile={isMobile}>
          <div className="flex flex-wrap gap-[6px]">
            {specializations.map((s, i) => (
              <span
                key={`${s}-${i}`}
                className="px-[9px] py-[3px] rounded-full bg-[#F7F8FA] text-[#475467] text-[11.5px] font-medium border border-[#E7EBF0]"
              >
                {s}
              </span>
            ))}
          </div>
        </Section>
      )}

      {(jurisdictions.length > 0 || languages.length > 0) && (
        <div className={`grid grid-cols-2 ${isMobile ? "gap-[14px]" : "gap-4"}`}>
          {jurisdictions.length > 0 && (
            <Section title={t("Jurisdictions")} mobile={isMobile}>
              <div className={`flex flex-col ${isMobile ? "gap-1" : "gap-[5px]"}`}>
                {jurisdictions.map((j, i) => (
                  <span
                    key={`${j.label}-${i}`}
                    className="inline-flex items-center gap-[5px] text-[13px] text-[#344054] font-medium"
                  >
                    <Flag {...j} />
                    {j.label}
                  </span>
                ))}
              </div>
            </Section>
          )}
          {languages.length > 0 && (
            <Section title={t("Languages")} mobile={isMobile}>
              <p className={`text-[13px] text-[#344054] ${isMobile ? "leading-[1.55]" : "leading-[1.6]"}`}>
                {languages.join(", ")}
              </p>
            </Section>
          )}
        </div>
      )}

      {/* License/trust box — detail-only field (1f / 1m). Second line is the
          real verification statement, never decorative. */}
      {l.license_number && (
        <div
          className={`bg-[#F8FAFD] border border-[#E6EAF2] rounded-[11px] flex items-center ${
            isMobile ? "px-[14px] py-3 gap-[10px]" : "px-[15px] py-[13px] gap-[11px]"
          }`}
        >
          <ShieldCheckIcon size={isMobile ? 16 : 17} />
          <div className="min-w-0">
            <div className={`font-semibold text-[#101828] ${isMobile ? "text-[12px]" : "text-[12.5px]"}`}>
              {t("License")} {l.license_number}
            </div>
            {verified && (
              <div className={`text-[#667085] mt-[1px] ${isMobile ? "text-[11px]" : "text-[11.5px]"}`}>
                {isMobile
                  ? t("Verified by TransferPitch")
                  : t("Credentials verified by TransferPitch")}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  // ── Mobile — artboard 1m: full-screen page, nav bar, sticky contact bar ──
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col md:hidden">
        {/* Nav bar */}
        <div className="flex items-center gap-[10px] px-[18px] py-[10px] border-b border-[#EEF1F6]">
          <button type="button" onClick={onClose} aria-label={t("Back")} className="cursor-pointer">
            <BackArrowIcon size={17} />
          </button>
          <span className="text-[14px] font-semibold text-[#101828]">
            {t("Lawyer profile")}
          </span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-[18px] py-5 flex flex-col gap-4">
          {/* Identity */}
          <div className="flex items-center gap-[13px]">
            <Avatar
              src={l.profile_picture_url}
              alt={l.name}
              fallbackText={l.name}
              size={58}
              initialsSize={19}
            />
            <div className="min-w-0 flex-1">
              <h2 className="text-[17px] font-bold text-[#101828] truncate">{l.name}</h2>
              {firmLine && <p className="text-[12.5px] text-[#667085] mt-[1px]">{firmLine}</p>}
              {badgeRow}
            </div>
          </div>

          {sections}
          {contactRow}
        </div>

        {/* Sticky contact bar (hidden on own profile) */}
        {!isSelf && (
          <div className="border-t border-[#EEF1F6] bg-white px-[18px] pt-3 pb-5">
            {ctaButton(true)}
          </div>
        )}
      </div>
    );
  }

  // ── Desktop — artboard 1f: side panel over the grid ──
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0"
        style={{ background: LAW_SCRIM.panel }}
        onClick={onClose}
      />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-[440px] bg-white shadow-[-12px_0_40px_rgba(16,24,40,0.18)] flex flex-col">
        {/* Panel header */}
        <div className="flex items-center justify-between px-[22px] py-4 border-b border-[#EEF1F6]">
          <span className="text-[12px] font-semibold text-[#98A2B3] uppercase tracking-[.6px]">
            {t("Lawyer profile")}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-[#667085] hover:text-[#101828] cursor-pointer"
            aria-label={t("Close")}
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {/* Panel body (scrollable — the handoff's overflow:hidden is a canvas
            artifact, see SPEC-HANDOFF open question 3) */}
        <div className="flex-1 overflow-y-auto p-[22px] flex flex-col gap-[18px]">
          {/* Identity */}
          <div className="flex items-center gap-[14px]">
            <Avatar
              src={l.profile_picture_url}
              alt={l.name}
              fallbackText={l.name}
              size={64}
              initialsSize={21}
            />
            <div className="min-w-0 flex-1">
              <h2 className="text-[18px] font-bold text-[#101828] truncate">{l.name}</h2>
              {firmLine && <p className="text-[13px] text-[#667085] mt-[2px]">{firmLine}</p>}
              {badgeRow}
            </div>
          </div>

          {/* Contact block: CTA (hidden on own profile) + opt-in contact row */}
          {(!isSelf || hasContacts) && (
            <div className="flex flex-col gap-[9px]">
              {!isSelf && ctaButton(false)}
              {contactRow}
            </div>
          )}

          {sections}
        </div>
      </div>
    </div>
  );
};

export default LawyerDetail;
